import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import aboutImgSmAvif from './assets/2310 clique.jpg?w=380&format=avif&quality=50&imagetools';
import aboutImgMdAvif from './assets/2310 clique.jpg?w=760&format=avif&quality=50&imagetools';
import aboutImgSmWebp from './assets/2310 clique.jpg?w=380&format=webp&quality=70&imagetools';
import aboutImgSmJpg from './assets/2310 clique.jpg?w=380&format=jpg&quality=70&imagetools';
import aboutImgMdWebp from './assets/2310 clique.jpg?w=760&format=webp&quality=70&imagetools';
import aboutImgMdJpg from './assets/2310 clique.jpg?w=760&format=jpg&quality=70&imagetools';
import logoImage80 from './assets/logo.jpg?w=80&format=jpg&imagetools';
import logoImage160 from './assets/logo.jpg?w=160&format=jpg&imagetools';
import logoImage240 from './assets/logo.jpg?w=240&format=jpg&imagetools';
import heroVideo from './assets/@2310tattoo κλείσε ραντεβού τώρα.mp4';
import heroPoster1200 from './assets/spin-background.webp';
import { Menu, X, Instagram, MapPin, Phone, Clock, Star, Palette, Award, Send } from 'lucide-react';
import AuthMenu from './components/auth/AuthMenu';
import RegisterModal from './components/auth/RegisterModal';
import LuckyWheelPromoModal from './components/LuckyWheelPromoModal';
import { useAuth } from './context/AuthContext';
import { loadFromStorage, saveToStorage } from './utils/storage';
import { useConsent } from './context/ConsentContext';
import { loadLanguage } from './i18n';
import { Helmet } from 'react-helmet-async';
import { useSpinWheelEnabled } from './hooks/useSpinWheelEnabled';
const ServicesSection = lazy(() => import('./components/ServicesSection'));
const GallerySection = lazy(() => import('./components/GallerySection'));
const FaqSection = lazy(() => import('./components/FaqSection'));

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isEnPage = location.pathname.replace(/\/+$/, '') === '/en';
  const { openManager } = useConsent();
  const { state } = useAuth();
  const spinWheelEnabled = useSpinWheelEnabled();
  const [showWheelPromo, setShowWheelPromo] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [shouldLoadHeroVideo, setShouldLoadHeroVideo] = useState(false);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [shouldLoadGallery, setShouldLoadGallery] = useState(false);
  const gallerySentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadServices, setShouldLoadServices] = useState(false);
  const servicesSentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoadFaq, setShouldLoadFaq] = useState(false);
  const faqSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'gallery', 'services', 'faq', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Lock language to the route: /en always renders English, / always renders Greek
  useEffect(() => {
    if (isEnPage) {
      loadLanguage('en');
    } else {
      loadLanguage('el');
    }
  }, [isEnPage]);

  // Show Lucky Wheel promo to unauthenticated users on homepage (once per user/device)
  useEffect(() => {
    if (state.loading) return;
    if (state.user) return;
    if (spinWheelEnabled === null || spinWheelEnabled === false) return; // wheel disabled by admin
    const hasSeen = loadFromStorage<boolean>('promo.wheel.shown.v1', false);
    const isAtTop = typeof window !== 'undefined' ? window.location.pathname === '/' : true;
    if (!hasSeen && isAtTop) {
      const tId = setTimeout(() => setShowWheelPromo(true), 800);
      return () => clearTimeout(tId);
    }
  }, [state.loading, state.user, spinWheelEnabled]);

  useEffect(() => {
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const reducedData = window.matchMedia('(prefers-reduced-data: reduce)').matches;
    if (isDesktop && !reducedData) {
      const idle = (cb: () => void) => (window as any).requestIdleCallback ? (window as any).requestIdleCallback(cb) : setTimeout(cb, 0);
      idle(() => setShouldLoadHeroVideo(true));
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadGallery(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    if (gallerySentinelRef.current) {
      observer.observe(gallerySentinelRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadServices(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    if (servicesSentinelRef.current) {
      observer.observe(servicesSentinelRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadFaq(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    if (faqSentinelRef.current) {
      observer.observe(faqSentinelRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const navItems = [
    { id: 'home', label: t('nav.home') },
    { id: 'about', label: t('nav.about') },
    { id: 'services', label: t('nav.services') },
    { id: 'gallery', label: t('nav.gallery') },
    { id: 'faq', label: t('nav.faq') },
    { id: 'contact', label: t('nav.contact') }
  ];
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const servicesKeys: string[] = ['fineLine', 'realistic', 'blackGray', 'color', 'coverUps', 'custom'];

  const faqKeys: string[] = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];

  // Removed Artists section in favor of FAQ and expanded services

  const instagramPermalinks: string[] = [
    // Replace with real public Instagram post permalinks from your account
    'https://www.instagram.com/p/DIVG4J7o7Wa/',
    'https://www.instagram.com/p/C2k7LJPNkbB/',
    'https://www.instagram.com/p/C-GSc_pulYG/',
    'https://www.instagram.com/p/C8fc_CCts5g/',
    'https://www.instagram.com/p/C8A5QCACtEh/',
    'https://www.instagram.com/p/C3bH3ImtLOy/'
  ];

  useEffect(() => {
    const instagram = (window as any).instgrm;
    if (instagram?.Embeds?.process) {
      instagram.Embeds.process();
    }
  }, []);

  return (
    <div className="bg-black text-white min-h-screen">
      <Helmet>
        <title>{isEnPage ? '210 Tattoo Athens | Tattoo Studio | Fine Line & Custom' : '210 Tattoo Athens | Τατουάζ Αθήνα | Fine Line & Custom'}</title>
        <html lang={isEnPage ? 'en' : 'el'} />
        <link rel="canonical" href={isEnPage ? 'https://210tattoo.gr/en' : 'https://210tattoo.gr/'} />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/" />
        <link rel="alternate" hrefLang="en" href="https://210tattoo.gr/en" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/" />
        {isEnPage && <meta name="description" content="210 Tattoo Athens, professional tattoo studio in Athens, Greece. Fine line, realistic & custom tattoos. Book your appointment today." />}
      </Helmet>
      <LuckyWheelPromoModal
        isOpen={showWheelPromo}
        onClose={() => {
          setShowWheelPromo(false);
          saveToStorage('promo.wheel.shown.v1', true);
        }}
        onSpinNow={() => {
          setShowWheelPromo(false);
          saveToStorage('promo.wheel.shown.v1', true);
          setShowRegister(true);
        }}
      />
      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onOpenSignIn={() => setShowRegister(false)}
        onRegistered={() => setShowRegister(false)}
      />
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b border-pink-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex-shrink-0 flex items-center">
              <button onClick={() => scrollToSection('home')} className="flex items-center gap-3">
                <picture>
                  <source srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} type="image/jpeg" />
                  <img src={logoImage80} srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} alt="210 Tattoo Studio" className="h-20 w-auto rounded-lg object-contain" width={80} height={80} loading="eager" decoding="async" />
                </picture>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                {/* Home direct link first */}
                <button
                  onClick={() => scrollToSection('home')}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 hover:text-pink-400 ${activeSection === 'home' ? 'text-pink-500 border-b-2 border-pink-500' : 'text-gray-300'}`}
                >
                  {t('nav.home')}
                </button>
                {/* Studio & Services dropdown */}
                <div className="relative"
                  onMouseEnter={() => setOpenDropdown('studio')}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    onClick={() => setOpenDropdown(openDropdown === 'studio' ? null : 'studio')}
                    className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400"
                    aria-haspopup="true"
                    aria-expanded={openDropdown === 'studio'}
                  >
                    {t('nav.studioMenu')}
                  </button>
                  {openDropdown === 'studio' && (
                    <div className="absolute left-0 mt-2 w-48 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                      <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.about')}</button>
                      <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.services')}</button>
                    </div>
                  )}
                </div>
                {/* Gallery */}
                <button
                  onClick={() => scrollToSection('gallery')}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 hover:text-pink-400 ${activeSection === 'gallery' ? 'text-pink-500 border-b-2 border-pink-500' : 'text-gray-300'}`}
                >
                  {t('nav.gallery')}
                </button>
                {/* Blog */}
                <a href="/blog" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400 transition-colors duration-200">{t('nav.blog')}</a>
                {/* FAQ & Contact dropdown */}
                <div className="relative"
                  onMouseEnter={() => setOpenDropdown('help')}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    onClick={() => setOpenDropdown(openDropdown === 'help' ? null : 'help')}
                    className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400"
                    aria-haspopup="true"
                    aria-expanded={openDropdown === 'help'}
                  >
                    {t('nav.helpMenu')}
                  </button>
                  {openDropdown === 'help' && (
                    <div className="absolute left-0 mt-2 w-56 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                      <button onClick={() => scrollToSection('faq')} className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.faq')}</button>
                      <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.contact')}</button>
                    </div>
                  )}
                </div>
                {/* Spin route (hidden when admin disables the wheel) */}
                {spinWheelEnabled !== false && (
                  <a href="/spin" className="px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-600 to-pink-700 rounded-lg hover:from-pink-700 hover:to-pink-800 shadow-lg hover:shadow-pink-500/25">{t('nav.spin')}</a>
                )}
                <div className="flex items-center gap-2 pl-4 border-l border-pink-600/20">
                  <a href="/" className={`px-2 py-1 text-xs rounded ${!isEnPage ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to Greek">ΕΛ</a>
                  <a href="/en" className={`px-2 py-1 text-xs rounded ${isEnPage ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to English">EN</a>
                </div>
                <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-pink-500/25">
                  <Instagram size={16} />
                  {t('nav.dmCta')}
                </a>
                <AuthMenu />
              </div>
            </div>

            {/* Mobile actions */}
            <div className="md:hidden flex items-center gap-2">
              <AuthMenu />
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-300 hover:text-white p-2" aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={isMenuOpen} aria-controls="mobile-menu">
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-menu" className="md:hidden bg-black border-t border-pink-600/20">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {(['home','about','services','gallery','faq','contact'] as const).map((id) => (
                <button key={id} onClick={() => scrollToSection(id)} className={`block px-3 py-2 text-base font-medium w-full text-left transition-colors duration-200 hover:text-pink-400 ${activeSection === id ? 'text-pink-500' : 'text-gray-300'}`}>{t(`nav.${id}` as any)}</button>
              ))}
              <a href="/blog" className="block px-3 py-2 text-base font-medium w-full text-left text-gray-300 hover:text-pink-400">{t('nav.blog')}</a>
              {spinWheelEnabled !== false && (
                <a href="/spin" className="block px-3 py-2 text-base font-medium w-full text-left text-pink-300 hover:text-pink-400">{t('nav.spin')}</a>
              )}
              <div className="flex items-center gap-2 mt-2 px-3">
                <a href="/" className={`px-2 py-1 text-xs rounded ${!isEnPage ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>ΕΛ</a>
                <a href="/en" className={`px-2 py-1 text-xs rounded ${isEnPage ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>EN</a>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 px-3 pb-3 items-center justify-items-center mx-3">
                <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-3 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 justify-self-center">
                  <Instagram size={16} />
                  {t('nav.dmCta')}
                </a>
                <div className="flex justify-center justify-self-center"><AuthMenu /></div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {shouldLoadHeroVideo ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            preload="metadata"
            src={heroVideo}
            autoPlay
            muted
            loop
            playsInline
            poster={heroPoster1200}
          />
        ) : (
          <div
            className="absolute inset-0 w-full h-full"
            style={{ backgroundImage: `url(${heroPoster1200})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            aria-hidden
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/80"></div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">
            {t('hero.title', 'Στούντιο Τατουάζ στην Αθήνα')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://www.instagram.com/210tattoo/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 transform hover:scale-105"
            >
              <Instagram size={20} />
              {t('hero.viewArt')}
            </a>
            <a
              href="#contact"
              className="border-2 border-pink-500 text-pink-500 hover:bg-pink-500 hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-3"
            >
              <Send size={20} />
              {t('hero.book')}
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('about.title')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <picture>
                <source srcSet={`${aboutImgMdAvif} 760w, ${aboutImgSmAvif} 380w`} type="image/avif" />
                <source srcSet={`${aboutImgMdWebp} 760w, ${aboutImgSmWebp} 380w`} type="image/webp" />
                <source srcSet={`${aboutImgMdJpg} 760w, ${aboutImgSmJpg} 380w`} type="image/jpeg" />
                <img
                  src={aboutImgSmJpg}
                  alt={t('about.altImage')}
                  className="rounded-lg shadow-2xl w-full h-auto"
                  style={{ aspectRatio: '380 / 475' }}
                  loading="lazy"
                  decoding="async"
                  width={380}
                  height={475}
                  sizes="(max-width: 400px) 100vw, 380px"
                />
              </picture>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-6 text-pink-500">{t('about.pillars')}</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {t('about.pillarsDescription')}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="bg-pink-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="text-white" size={24} />
                  </div>
                  <h4 className="font-semibold text-white">{t('about.reviewsCount')}</h4>
                  <p className="text-gray-400">{t('about.reviews')}</p>
                </div>
                <div className="text-center">
                  <div className="bg-pink-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="text-white" size={24} />
                  </div>
                  <h4 className="font-semibold text-white">{t('about.hygieneFirst')}</h4>
                  <p className="text-gray-400">{t('about.certifiedInks')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-black">
        <div ref={servicesSentinelRef} />
        {shouldLoadServices ? (
          <Suspense fallback={<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">Loading services…</div>}>
            <ServicesSection />
          </Suspense>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">Services will load on scroll…</div>
        )}
      </section>


      {/* Gallery Section */}
      <section id="gallery" className="py-20 bg-black">
        <div ref={gallerySentinelRef} />
        {shouldLoadGallery ? (
          <Suspense fallback={<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">Loading gallery…</div>}>
            <GallerySection />
          </Suspense>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">Gallery will load on scroll…</div>
        )}
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-black">
        <div ref={faqSentinelRef} />
        {shouldLoadFaq ? (
          <Suspense fallback={<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">Loading FAQ…</div>}>
            <FaqSection />
          </Suspense>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-400">FAQ will load on scroll…</div>
        )}
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t('contact.title')}</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-8 text-white">{t('contact.info')}</h3>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-pink-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{t('contact.address')}</h4>
                    <p className="text-gray-300">Αναστασίου Γενναδίου 34, Γκύζη, Αθήνα 114 72</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-pink-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <Phone className="text-white" size={20} />
                  </div>
                <div>
                  <h4 className="font-semibold text-white">{t('contact.phone')}</h4>
                  <p className="text-gray-300">+30 694 066 4049</p>
                </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-pink-600 w-12 h-12 rounded-full flex items-center justify-center">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{t('contact.hours')}</h4>
                    <p className="text-gray-300">{t('contact.monday')}</p>
                    <p className="text-gray-300">{t('contact.weekdays')}</p>
                    <p className="text-gray-300">{t('contact.saturday')}</p>
                    <p className="text-gray-300">{t('contact.sunday')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <a
                  href="https://www.instagram.com/210tattoo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-3 w-fit shadow-lg hover:shadow-pink-500/25 transform hover:scale-105"
                >
                  <Instagram size={20} />
                  {t('contact.dmCta')}
                </a>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg overflow-hidden shadow-lg">
            <h3 className="text-2xl font-bold px-8 pt-8 mb-4 text-white">{t('contact.studioLocation')}</h3>
            <div className="px-8 pb-8">
    <div className="rounded-lg overflow-hidden border border-gray-700">
      <iframe
        title={t('contact.mapTitle')}
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7923.244997929856!2d23.738033346346533!3d37.9924878433975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14a1bda7a3eb70af%3A0xde9548947ea138f3!2s210tattoo%20Athens%20Tattoo%20Studio%2C%20Your%20Destination%20for%20Every%20Style!5e0!3m2!1sen!2sgr!4v1755086022504!5m2!1sen!2sgr"
        width="600"
        height="450"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  </div>
</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-3xl font-bold text-pink-500 mb-4" itemProp="name">{t('footer.studio')}</p>
            <p className="text-gray-400 mb-4">{t('footer.tagline')}</p>
            <address className="not-italic mb-6 space-y-1" itemScope itemType="https://schema.org/TattooParlor">
              <span className="sr-only" itemProp="name">210 Tattoo Athens</span>
              <p className="text-gray-400" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <span itemProp="streetAddress">Anastasiou Gennadiou 34</span>,{' '}
                <span itemProp="addressLocality">Athens</span>,{' '}
                <span itemProp="postalCode">114 72</span>,{' '}
                <span itemProp="addressCountry">GR</span>
              </p>
              <p className="text-gray-400">
                Τηλ:{' '}
                <a href="tel:+306940664049" className="hover:text-pink-400 transition-colors" itemProp="telephone">
                  +30 694 066 4049
                </a>
              </p>
            </address>
            <div className="flex justify-center space-x-6 mb-8">
              <a
                href="https://www.instagram.com/210tattoo/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-500 transition-colors duration-200"
                aria-label={t('footer.instagramLabel', 'Visit our Instagram profile')}
              >
                <Instagram size={24} />
              </a>
            </div>
            <nav className="mb-8">
              <ul className="flex justify-center gap-6 text-sm">
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    {t('footer.privacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    {t('footer.cookies')}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    {t('footer.terms')}
                  </Link>
                </li>
                <li>
                  <button type="button" onClick={openManager} className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    {t('consent.manage')}
                  </button>
                </li>
                <li>
                  <Link to="/athens-tattoo-studio-locations" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    Περιοχές που Εξυπηρετούμε
                  </Link>
                </li>
                <li>
                  <Link to="/dashboard" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">
                    Dashboard
                  </Link>
                </li>
              </ul>
            </nav>
            <div className="border-t border-gray-800 pt-8">
              <p className="text-gray-300 text-sm">
                {t('footer.rights')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;